import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { BadgeCheck } from "lucide-react";
import { useCart } from "../context/CartContext";
import { formatMoney, originalPrice, DISCOUNT_LABEL } from "../lib/apiClient";

export default function ProductCard({ product, index = 0 }) {
  const { addItem, saleOn, bogo } = useCart();
  const navigate = useNavigate();
  const hasVariants = product.has_variants || (product.strengths?.length > 1);

  const add = (e) => {
    e.preventDefault();
    if (hasVariants) { navigate(`/product/${product.id}`); return; }
    addItem(product);
    toast.success(`${product.name} added to cart`);
  };

  return (
    <Link
      to={`/product/${product.id}`}
      data-testid={`product-card-${product.id}`}
      className="product-card group block rounded-2xl card-soft overflow-hidden animate-fade-up"
      style={{ animationDelay: `${index * 50}ms` }}
    >
      <div className="relative aspect-square bg-[#f6f1e6] overflow-hidden">
        {bogo ? (
          <span data-testid={`bogo-badge-${product.id}`} className="absolute top-3 left-3 z-10 pill px-3 py-1 text-[11px] font-display font-bold text-[#1a1815]" style={{ background: "var(--gold)" }}>
            BUY 1 GET 1 FREE
          </span>
        ) : saleOn ? (
          <span data-testid={`discount-badge-${product.id}`} className="absolute top-3 left-3 z-10 pill px-3 py-1 text-[11px] font-display font-bold text-[#1a1815]" style={{ background: "var(--gold)" }}>
            {DISCOUNT_LABEL}
          </span>
        ) : null}
        {product.coa && product.coa.purity && (
          <span
            data-testid={`coa-badge-${product.id}`}
            className="absolute top-3 right-3 z-10 inline-flex items-center gap-1 rounded-full border border-[#cfe6cf] bg-[#f3faf3]/95 backdrop-blur-sm px-2.5 py-1 text-[11px] font-display font-bold text-[#276434] shadow-sm"
          >
            <BadgeCheck className="w-3.5 h-3.5 shrink-0" aria-hidden="true" />
            COA 99%+
          </span>
        )}
        <img
          src={product.image_url}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-[1.04] transition-transform duration-500"
        />
      </div>
      <div className="p-5">
        <h3 className="font-display font-bold text-lg leading-tight">{product.name}</h3>
        {product.subtitle ? (
          <p className="text-sm text-muted-foreground mt-0.5">{product.subtitle}</p>
        ) : (
          <p className="text-sm text-muted-foreground mt-0.5">{product.category}</p>
        )}
        <div className="flex items-end justify-between mt-4">
          <p className="text-sm text-muted-foreground">
            {hasVariants ? "From " : ""}
            {saleOn && <span className="line-through mr-1.5" data-testid={`product-original-price-${product.id}`}>{formatMoney(originalPrice(product.price))}</span>}
            <span className="font-display font-bold text-foreground text-lg">{formatMoney(product.price)}</span>
          </p>
          <button
            onClick={add}
            data-testid={`add-to-cart-button-${product.id}`}
            className="pill px-4 py-2 text-sm font-semibold btn-ink opacity-0 group-hover:opacity-100 transition-opacity"
          >
            {hasVariants ? "Options" : "Add"}
          </button>
        </div>
      </div>
    </Link>
  );
}
